import React, { useState, useEffect } from 'react';

const TravelPreferenceModal = ({ show, onHide, onConfirm, initialPreferences = {} }) => {
  const [preferences, setPreferences] = useState({
    sightseeing: 25,
    nature: 25,
    culture: 20,
    Restaurant: 20,
    entertainment: 10,
    ...initialPreferences
  });

  const [animationStep, setAnimationStep] = useState(0);

  const preferenceConfig = {
    sightseeing: {
      label: 'Sightseeing',
      color: '#007AFF',
      description: 'Famous landmarks and attractions'
    },
    nature: {
      label: 'Nature & Views',
      color: '#34C759',
      description: 'Parks, mountains, and scenic spots'
    },
    culture: {
      label: 'Cultural Sites',
      color: '#AF52DE',
      description: 'Museums, galleries, and heritage'
    },
    Restaurant: {
      label: 'Food & Dining',
      color: '#FF9500',
      description: 'Restaurants and local cuisine'
    },
    entertainment: {
      label: 'Entertainment',
      color: '#FF3B30',
      description: 'Shows, nightlife, and activities'
    }
  };

  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        setAnimationStep(prev => prev < Object.keys(preferences).length ? prev + 1 : prev);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setAnimationStep(0);
    }
  }, [show, animationStep, preferences]);

  const handleSliderChange = (key, value) => {
    const newPreferences = { ...preferences };
    const otherKeys = Object.keys(newPreferences).filter(k => k !== key);
    const otherSum = otherKeys.reduce((sum, k) => sum + newPreferences[k], 0);
    
    newPreferences[key] = value;
    
    if (value + otherSum > 100) {
      const excess = (value + otherSum) - 100;
      otherKeys.forEach(k => {
        const reduction = (newPreferences[k] / otherSum) * excess;
        newPreferences[k] = Math.max(0, Math.round(newPreferences[k] - reduction));
      });
    }
    
    const currentSum = Object.values(newPreferences).reduce((sum, val) => sum + val, 0);
    if (currentSum !== 100) {
      const adjustment = 100 - currentSum;
      const adjustKey = otherKeys[0];
      newPreferences[adjustKey] = Math.max(0, newPreferences[adjustKey] + adjustment);
    }
    
    setPreferences(newPreferences);
  };

  const handleConfirm = () => {
    onConfirm(preferences);
    onHide();
  };

  const totalPercentage = Object.values(preferences).reduce((sum, val) => sum + val, 0);

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      opacity: show ? 1 : 0,
      visibility: show ? 'visible' : 'hidden',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      backdropFilter: 'blur(20px)'
    },
    modal: {
      backgroundColor: '#FFFFFF',
      borderRadius: '16px',
      maxWidth: '540px',
      width: '90%',
      maxHeight: '80vh',
      overflow: 'hidden',
      boxShadow: '0 24px 48px rgba(0, 0, 0, 0.15)',
      transform: show ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(20px)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      padding: '32px 32px 24px',
      textAlign: 'center',
      borderBottom: '1px solid #F2F2F7'
    },
    title: {
      fontSize: '22px',
      fontWeight: '600',
      color: '#1D1D1F',
      marginBottom: '8px',
      margin: 0,
      letterSpacing: '-0.02em'
    },
    subtitle: {
      fontSize: '16px',
      color: '#86868B',
      margin: 0,
      fontWeight: '400',
      lineHeight: '1.4'
    },
    body: {
      padding: '24px 32px',
      maxHeight: '50vh',
      overflowY: 'auto'
    },
    preferenceItem: {
      marginBottom: '24px',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    },
    preferenceHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '12px'
    },
    preferenceInfo: {
      flex: 1
    },
    preferenceLabel: {
      fontSize: '17px',
      fontWeight: '590',
      color: '#1D1D1F',
      marginBottom: '2px',
      letterSpacing: '-0.01em'
    },
    preferenceDescription: {
      fontSize: '15px',
      color: '#86868B',
      margin: 0,
      fontWeight: '400'
    },
    percentageDisplay: {
      fontSize: '17px',
      fontWeight: '590',
      minWidth: '50px',
      textAlign: 'right',
      color: '#1D1D1F'
    },
    sliderContainer: {
      position: 'relative',
      height: '28px',
      display: 'flex',
      alignItems: 'center'
    },
    sliderTrack: {
      position: 'absolute',
      width: '100%',
      height: '3px',
      backgroundColor: '#E5E5EA',
      borderRadius: '2px'
    },
    sliderProgress: {
      position: 'absolute',
      height: '3px',
      borderRadius: '2px',
      transition: 'width 0.2s ease'
    },
    slider: {
      width: '100%',
      height: '28px',
      background: 'transparent',
      outline: 'none',
      appearance: 'none',
      WebkitAppearance: 'none',
      cursor: 'pointer',
      position: 'relative',
      zIndex: 1
    },
    totalIndicator: {
      padding: '16px',
      borderRadius: '12px',
      textAlign: 'center',
      marginTop: '24px',
      marginBottom: '16px'
    },
    totalText: {
      fontSize: '15px',
      fontWeight: '590',
      margin: 0
    },
    actions: {
      display: 'flex',
      padding: '20px 32px 32px',
      gap: '12px',
      borderTop: '1px solid #F2F2F7'
    },
    button: {
      flex: 1,
      padding: '11px 16px',
      borderRadius: '8px',
      fontSize: '17px',
      fontWeight: '590',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontFamily: 'inherit'
    },
    primaryButton: {
      backgroundColor: '#007AFF',
      color: 'white'
    },
    secondaryButton: {
      backgroundColor: '#F2F2F7',
      color: '#1D1D1F'
    },
    resetButton: {
      background: 'none',
      border: 'none',
      color: '#007AFF',
      padding: '8px',
      fontSize: '15px',
      cursor: 'pointer',
      fontFamily: 'inherit',
      marginTop: '8px'
    }
  };

  // 自定义滑块样式
  const sliderThumbStyle = `
    input[type="range"]::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #FFFFFF;
      border: 2px solid var(--thumb-color);
      cursor: pointer;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      transition: all 0.2s ease;
    }
    
    input[type="range"]::-webkit-slider-thumb:hover {
      transform: scale(1.1);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }
    
    input[type="range"]::-moz-range-thumb {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #FFFFFF;
      border: 2px solid var(--thumb-color);
      cursor: pointer;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
  `;

  if (!show) return null;

  return (
    <>
      <style>{sliderThumbStyle}</style>
      <div style={styles.overlay} onClick={onHide}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={styles.header}>
            <h2 style={styles.title}>Travel Preferences</h2>
            <p style={styles.subtitle}>
              Customize your Montreal experience by adjusting these categories
            </p>
          </div>

          <div style={styles.body}>
            {Object.entries(preferenceConfig).map(([key, config], index) => (
              <div 
                key={key}
                style={{
                  ...styles.preferenceItem,
                  transform: index < animationStep ? 'translateY(0)' : 'translateY(12px)',
                  opacity: index < animationStep ? 1 : 0,
                  transition: `all 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.08}s`
                }}
              >
                <div style={styles.preferenceHeader}>
                  <div style={styles.preferenceInfo}>
                    <div style={styles.preferenceLabel}>{config.label}</div>
                    <div style={styles.preferenceDescription}>{config.description}</div>
                  </div>
                  <div style={styles.percentageDisplay}>
                    {preferences[key]}%
                  </div>
                </div>
                
                <div style={styles.sliderContainer}>
                  <div style={styles.sliderTrack}></div>
                  <div style={{
                    ...styles.sliderProgress,
                    width: `${preferences[key]}%`,
                    backgroundColor: config.color
                  }}></div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={preferences[key]}
                    onChange={(e) => handleSliderChange(key, parseInt(e.target.value))}
                    style={{
                      ...styles.slider,
                      '--thumb-color': config.color
                    }}
                  />
                </div>
              </div>
            ))}

            <div style={{
              ...styles.totalIndicator,
              backgroundColor: totalPercentage === 100 ? '#E8F5E8' : '#FFF8E1',
              border: `1px solid ${totalPercentage === 100 ? '#D4EDDA' : '#FFE0B2'}`
            }}>
              <div style={{
                ...styles.totalText,
                color: totalPercentage === 100 ? '#2E7D32' : '#F57C00'
              }}>
                Total: {totalPercentage}%
                {totalPercentage === 100 ? ' — Perfect' : ' — Adjust to 100%'}
              </div>
            </div>

            <div style={{ textAlign: 'center' }}>
              <button 
                style={styles.resetButton}
                onClick={() => setPreferences({
                  sightseeing: 25,
                  nature: 25,
                  culture: 20,
                  Restaurant: 20,
                  entertainment: 10
                })}
                onMouseEnter={(e) => {
                  e.target.style.opacity = '0.7';
                }}
                onMouseLeave={(e) => {
                  e.target.style.opacity = '1';
                }}
              >
                Reset to Default
              </button>
            </div>
          </div>

          <div style={styles.actions}>
            <button 
              style={{
                ...styles.button,
                ...styles.secondaryButton
              }}
              onClick={onHide}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#E8E8ED';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#F2F2F7';
              }}
            >
              Cancel
            </button>
            <button 
              style={{
                ...styles.button,
                ...styles.primaryButton,
                opacity: totalPercentage === 100 ? 1 : 0.5,
                cursor: totalPercentage === 100 ? 'pointer' : 'not-allowed'
              }}
              onClick={handleConfirm}
              disabled={totalPercentage !== 100}
              onMouseEnter={(e) => {
                if (totalPercentage === 100) {
                  e.target.style.backgroundColor = '#0056D6';
                }
              }}
              onMouseLeave={(e) => {
                if (totalPercentage === 100) {
                  e.target.style.backgroundColor = '#007AFF';
                }
              }}
            >
              {totalPercentage === 100 ? 'Apply Preferences' : `Adjust by ${Math.abs(100 - totalPercentage)}%`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default TravelPreferenceModal;

